import React, { useCallback } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import Tooltip from '../../ui/Tooltip'
import BellOnIcon from '../../ui/KpiCard/icons/BellOnIcon'
import ClockIcon from '../../ui/icons/ClockIcon'
import CircleCheckIcon from '../../ui/icons/CircleCheckIcon'
import StatusRow from './StatusRow'
import { MAX_PER_CATEGORY } from './constants'

const MAX_TOOLTIP = 'Vous avez atteint la limite de 5 statuts.'

const categoryIconFor = value => {
  if (value === 'in_progress')
    return ClockIcon
  if (value === 'done')
    return CircleCheckIcon
  return BellOnIcon
}

const SECTION_CLASS = 'tw:flex! tw:flex-col tw:gap-4'
const HEADER_CLASS = 'tw:flex! tw:items-center tw:gap-2'
const ICON_CLASS = 'tw:h-4 tw:w-4 tw:text-gray-600'
const TITLE_CLASS = 'tw:text-sm! tw:font-medium! tw:text-gray-900! tw:m-0!'
const COUNT_CLASS = 'tw:text-xs tw:text-gray-700'
const LIST_CLASS = 'tw:flex tw:flex-col tw:gap-2'
// Lift the dragged clone above the drawer panel (z-1050) since it is portaled to body.
const DRAGGING_CLASS = 'tw:z-[1100]'
const ADD_CLASS = 'tw:inline-flex tw:items-center tw:self-start tw:text-sm tw:text-gray-700 '
  + 'tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer '
  + 'tw:disabled:opacity-50 tw:disabled:cursor-not-allowed'

const CategorySection = ({
  category, rows, disabled, errors, onNameChange, onColorChange, onDelete, onAdd
}) => {
  const handleAdd = useCallback(() => onAdd(category.value), [onAdd, category.value])
  const atMax = rows.length >= MAX_PER_CATEGORY
  const CategoryIcon = categoryIconFor(category.value)

  const addButton = (
    <button className={ADD_CLASS} disabled={disabled || atMax} type="button" onClick={handleAdd}>
      <i aria-hidden="true" className="fas fa-plus tw:mr-2" />
      <span>Ajouter un statut</span>
    </button>
  )

  return (
    <section className={SECTION_CLASS}>
      <header className={HEADER_CLASS}>
        <CategoryIcon className={ICON_CLASS} />
        <h3 className={TITLE_CLASS}>{category.label}</h3>
        <span className={COUNT_CLASS}>{`${rows.length}/${MAX_PER_CATEGORY}`}</span>
      </header>
      <div className={LIST_CLASS}>
        <Droppable droppableId={category.value}>
          {provided => (
            <div ref={provided.innerRef} className={LIST_CLASS} {...provided.droppableProps}>
              {rows.map((row, index) => (
                <Draggable key={row.key} draggableId={row.key} index={index} isDragDisabled={disabled}>
                  {(dragProvided, dragSnapshot) => {
                    const content = (
                      <div
                        ref={dragProvided.innerRef}
                        className={dragSnapshot.isDragging ? DRAGGING_CLASS : ''}
                        {...dragProvided.draggableProps}
                      >
                        <StatusRow
                          colors={category.colors}
                          disabled={disabled}
                          dragHandleProps={dragProvided.dragHandleProps}
                          error={errors[row.key]}
                          row={row}
                          onColorChange={onColorChange}
                          onDelete={onDelete}
                          onNameChange={onNameChange}
                        />
                      </div>
                    )
                    return dragSnapshot.isDragging ? createPortal(content, document.body) : content
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        {atMax ? (
          <Tooltip className="tw:flex tw:items-center tw:self-start" content={MAX_TOOLTIP}>
            {addButton}
          </Tooltip>
        ) : addButton}
      </div>
    </section>
  )
}

CategorySection.propTypes = {
  category: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    colors: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  disabled: PropTypes.bool.isRequired,
  errors: PropTypes.objectOf(PropTypes.shape({})).isRequired,
  onAdd: PropTypes.func.isRequired,
  onColorChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired })).isRequired
}

export default CategorySection
